import { Link } from "react-router";

type LineCTAButtonProps = {
  href: string;
  text: string;
  variant?: "default" | "fixed" | "compact";
  color?: "line" | "pink" | "red" | "shegate" | "blue" | "lime" | "navy" | "orange";
  className?: string;
  /** 内部リンクの場合 true */
  internal?: boolean;
  /** クリック時のコールバック */
  onClick?: () => void;
  /** カスタムアイコン（LINEアイコンの代わりに表示） */
  icon?: React.ReactNode;
  /** カスタム高さ（例: "48px"） */
  height?: string;
  /** カスタムフォントサイズ（例: "13px"） */
  fontSize?: string;
  /** バッジテキスト（アイコンの代わりに表示） */
  badge?: string;
  /** ピル型（角丸を大きく） */
  pill?: boolean;
};

const COLOR_CONFIG = {
  line: {
    gradient: "linear-gradient(135deg, #2DCE54 0%, #1DB446 100%)",
    shadow: "#019733",
    text: "white",
    stroke: "white",
  },
  pink: {
    gradient: "linear-gradient(135deg, #FF6B9D 0%, #E91E7A 100%)",
    shadow: "#B8155E",
    text: "white",
    stroke: "white",
  },
  red: {
    gradient: "linear-gradient(135deg, #E53935 0%, #B71C1C 100%)",
    shadow: "#7F0000",
    text: "white",
    stroke: "white",
  },
  shegate: {
    gradient: "linear-gradient(135deg, var(--color-shegate-accent) 0%, #E53935 100%)",
    shadow: "#0a4a6b",
    text: "white",
    stroke: "white",
  },
  blue: {
    gradient: "linear-gradient(135deg, var(--color-shegate-accent) 0%, #3f7daa 100%)",
    shadow: "#0a4a6b",
    text: "white",
    stroke: "white",
  },
  lime: {
    gradient: "linear-gradient(135deg, #F1FACC 0%, #C7FB9E 100%)",
    shadow: "#9ED86E",
    text: "#3A5A20",
    stroke: "#3A5A20",
  },
  navy: {
    gradient: "linear-gradient(135deg, #1C2750 0%, #2A3660 100%)",
    shadow: "#0F1830",
    text: "white",
    stroke: "white",
  },
  orange: {
    gradient: "linear-gradient(to right, #f6582d 20%, #f5a028 100%)",
    shadow: "#c44420",
    text: "white",
    stroke: "white",
  },
};

export const LINE_ICON_PATH =
  "M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314";

export function LineCTAButton({
  href,
  text,
  variant = "default",
  color = "line",
  className = "",
  internal = false,
  onClick,
  icon,
  height,
  fontSize,
  badge,
  pill = false,
}: LineCTAButtonProps) {
  const colors = COLOR_CONFIG[color];
  const radius = pill ? "rounded-full" : "rounded-[10px]";
  const showLineIcon = color === "line" && !icon && !badge;
  const isHashLink = internal && href.startsWith("#");

  // Compact variant for header
  if (variant === "compact") {
    const CompactContent = (
      <>
        {/* Shine Effect (plays once) */}
        <div
          className="absolute inset-0 animate-shine-once"
          style={{
            background: "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.4) 50%, transparent 60%)",
            backgroundSize: "200% 100%",
          }}
        />
        {/* LINE Icon */}
        {showLineIcon && (
          <svg width="14" height="14" viewBox="0 0 24 24" fill={colors.text} className="relative mr-1">
            <path d={LINE_ICON_PATH} />
          </svg>
        )}
        <span
          className="relative text-xs font-bold tracking-wide whitespace-nowrap"
          style={{ fontFamily: "YakuHanJP, 'Zen Kaku Gothic Antique', sans-serif", color: colors.text }}
        >
          {text}
        </span>
      </>
    );

    const compactClassName = `group relative inline-flex items-center justify-center h-8 px-3 rounded-md overflow-hidden transition-all duration-150 ease-out hover:brightness-110 active:scale-[0.98] ${className}`;

    if (isHashLink) {
      return (
        <a href={href} className={compactClassName} style={{ background: colors.gradient }} onClick={onClick}>
          {CompactContent}
        </a>
      );
    }

    if (internal) {
      return (
        <Link to={href} className={compactClassName} style={{ background: colors.gradient }} onClick={onClick}>
          {CompactContent}
        </Link>
      );
    }

    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={compactClassName}
        style={{ background: colors.gradient }}
        onClick={onClick}
      >
        {CompactContent}
      </a>
    );
  }

  if (variant === "fixed") {
    const buttonHeight = height || "56px";

    const FixedContent = (
      <>
        {/* Shadow */}
        <div
          className={`absolute top-[4px] left-0 right-0 bottom-0 ${radius}`}
          style={{ background: colors.shadow, height: buttonHeight }}
        />
        {/* Button */}
        <div
          className={`absolute top-0 left-0 right-0 ${radius} flex items-center justify-center overflow-hidden`}
          style={{ background: colors.gradient, height: buttonHeight }}
        >
          {/* Shine Effect */}
          <div
            className="absolute inset-0 animate-shine"
            style={{
              background: "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.4) 50%, transparent 60%)",
              backgroundSize: "200% 100%",
            }}
          />
          {/* Badge */}
          {badge && (
            <span
              className="mr-2 px-2 py-0.5 rounded text-[12px] font-bold bg-white/90"
              style={{ color: color === "lime" ? "#3A5A20" : "#1DB446" }}
            >
              {badge}
            </span>
          )}
          {/* Custom Icon */}
          {icon && !badge && (
            <span className="mr-2">
              {icon}
            </span>
          )}
          {/* LINE Icon */}
          {showLineIcon && (
            <svg width="20" height="20" viewBox="0 0 24 24" fill={colors.text} className="mr-2">
              <path d={LINE_ICON_PATH} />
            </svg>
          )}
          <span
            className="font-bold tracking-[0.5px]"
            style={{ fontFamily: "YakuHanJP, 'Zen Kaku Gothic Antique', sans-serif", color: colors.text, fontSize: fontSize || "15px" }}
          >
            {text}
          </span>
          {/* Arrow */}
          <svg width="10" height="15" viewBox="0 0 9 14" fill="none" className="ml-2">
            <path d="M1 1L7 7L1 13" stroke={colors.stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </>
    );

    const fixedClassName = `group relative flex items-center justify-center w-full rounded-[10px] transition-all duration-150 ease-out active:scale-[0.98] animate-cta-pulse ${className}`;

    const fixedStyle = { height: `calc(${buttonHeight} + 4px)` };

    if (isHashLink) {
      return (
        <a href={href} className={fixedClassName} style={fixedStyle} onClick={onClick}>
          {FixedContent}
        </a>
      );
    }

    if (internal) {
      return (
        <Link to={href} className={fixedClassName} style={fixedStyle} onClick={onClick}>
          {FixedContent}
        </Link>
      );
    }

    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={fixedClassName}
        style={fixedStyle}
        onClick={onClick}
      >
        {FixedContent}
      </a>
    );
  }

  const DefaultContent = (
    <>
      {/* Shadow */}
      <div
        className={`absolute top-[6px] md:top-[7.14px] left-0 right-0 bottom-0 ${radius} transition-all duration-150 ease-out group-hover:top-[10px] group-hover:shadow-lg group-active:top-[4px]`}
        style={{ background: colors.shadow }}
      />
      {/* Button */}
      <div
        className={`absolute top-0 left-0 right-0 h-[64px] md:h-[79.56px] ${radius} flex items-center justify-center transition-all duration-150 ease-out group-hover:brightness-110 group-active:brightness-95 overflow-hidden`}
        style={{ background: colors.gradient }}
      >
        {/* Shine Effect */}
        <div
          className="absolute inset-0 animate-shine"
          style={{
            background: "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.4) 50%, transparent 60%)",
            backgroundSize: "200% 100%",
          }}
        />
        {/* Badge */}
        {badge && (
          <span
            className="mr-2 md:mr-3 px-2 py-0.5 rounded text-[12px] md:text-[12px] font-bold bg-white/90"
            style={{ color: color === "lime" ? "#3A5A20" : "#1DB446" }}
          >
            {badge}
          </span>
        )}
        {/* Custom Icon */}
        {icon && !badge && (
          <span className="mr-2 md:mr-3 transition-transform duration-150 group-hover:scale-110">
            {icon}
          </span>
        )}
        {/* LINE Icon */}
        {showLineIcon && (
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill={colors.text}
            className="mr-2 md:mr-3 transition-transform duration-150 group-hover:scale-110"
          >
            <path d={LINE_ICON_PATH} />
          </svg>
        )}
        <span
          className="text-[14px] md:text-[20.4px] font-bold tracking-[0.5px] md:tracking-[1.02px]"
          style={{ fontFamily: "YakuHanJP, 'Zen Kaku Gothic Antique', sans-serif", color: colors.text }}
        >
          {text}
        </span>
        {/* Arrow */}
        <svg
          width="9"
          height="14"
          viewBox="0 0 9 14"
          fill="none"
          className="ml-2 md:ml-3 transition-transform duration-150 group-hover:translate-x-1"
        >
          <path d="M1 1L7 7L1 13" stroke={colors.stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </>
  );

  const defaultClassName = "group relative inline-flex items-center justify-center w-full max-w-[498.78px] h-[70px] md:h-[86.7px] transition-transform duration-150 ease-out hover:-translate-y-1 active:translate-y-0.5 animate-cta-pulse";

  if (isHashLink) {
    return (
      <div className={`flex justify-center px-4 md:px-0 ${className}`}>
        <a href={href} className={defaultClassName} onClick={onClick}>
          {DefaultContent}
        </a>
      </div>
    );
  }

  if (internal) {
    return (
      <div className={`flex justify-center px-4 md:px-0 ${className}`}>
        <Link to={href} className={defaultClassName} onClick={onClick}>
          {DefaultContent}
        </Link>
      </div>
    );
  }

  return (
    <div className={`flex justify-center px-4 md:px-0 ${className}`}>
      <a href={href} target="_blank" rel="noopener noreferrer" className={defaultClassName} onClick={onClick}>
        {DefaultContent}
      </a>
    </div>
  );
}
